/**
 * FormField Component
 * Reusable form field component for settings pages
 */

import { useRef, ChangeEvent } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import type { PageField } from '../types';
import { readFileAsDataUrl } from '../utils';

interface FormFieldProps {
    field: PageField;
    value: string | number | string[];
    onChange: (value: string | number | string[]) => void;
    onFileChange?: (value: string) => void;
    disabled?: boolean;
    className?: string;
}

export function FormField({ field, value, onChange, onFileChange, disabled = false, className }: FormFieldProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const multiImageInputRef = useRef<HTMLInputElement>(null);

    const mergedClassName = className || field.className;

    // Handle text/number field
    if (field.type === 'text' || field.type === 'number') {
        return (
            <div className={mergedClassName}>
                <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
                    {field.label}
                </label>
                <input
                    id={field.name}
                    type={field.type}
                    value={value as string | number}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
                    placeholder={field.placeholder}
                    disabled={disabled}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:opacity-50 disabled:bg-gray-100"
                />
                {field.helperText && (
                    <p className="text-xs text-gray-500 mt-1">{field.helperText}</p>
                )}
            </div>
        );
    }

    // Handle textarea
    if (field.type === 'textarea') {
        return (
            <div className={mergedClassName}>
                <label htmlFor={field.name} className="block text-sm font-medium text-gray-700">
                    {field.label}
                </label>
                <textarea
                    id={field.name}
                    value={value as string}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                    rows={field.rows || 4}
                    placeholder={field.placeholder}
                    disabled={disabled}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:opacity-50 disabled:bg-gray-100"
                />
                {field.helperText && (
                    <p className="text-xs text-gray-500 mt-1">{field.helperText}</p>
                )}
            </div>
        );
    }

    // Handle file upload with preview
    if (field.type === 'file') {
        const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            try {
                const dataUrl = await readFileAsDataUrl(file);
                onChange(dataUrl);
                if (onFileChange) {
                    onFileChange(dataUrl);
                }
            } catch (error) {
                console.error('Failed to read file', error);
            }
        };

        return (
            <div className={mergedClassName}>
                <label className="block text-sm font-medium text-gray-700">{field.label}</label>
                <div className="mt-1 space-y-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={field.accept}
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={disabled}
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={disabled}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm transition-colors disabled:opacity-50"
                    >
                        <Upload className="w-4 h-4" />
                        เลือกไฟล์
                    </button>
                    {value && typeof value === 'string' && value.length > 0 && (
                        <div className="relative inline-block">
                            <p className="text-xs text-gray-500 mb-1">{field.previewLabel || 'ตัวอย่าง'}</p>
                            <img
                                src={value}
                                alt="Preview"
                                className="max-h-32 rounded-md border object-contain"
                            />
                            <button
                                type="button"
                                onClick={() => onChange('')}
                                disabled={disabled}
                                className="absolute top-6 right-0 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    )}
                    {field.helperText && (
                        <p className="text-xs text-gray-500">{field.helperText}</p>
                    )}
                </div>
            </div>
        );
    }

    // Handle multi-image array
    if (field.type === 'multi-image') {
        const images = Array.isArray(value) ? value : [];

        const handleAddImages = async (e: ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length === 0) return;

            const newImages: string[] = [];
            for (const file of files) {
                try {
                    const dataUrl = await readFileAsDataUrl(file);
                    newImages.push(dataUrl);
                } catch (error) {
                    console.error('Failed to read file', error);
                }
            }

            if (newImages.length > 0) {
                onChange([...images, ...newImages]);
            }
        };

        const handleRemoveImage = (index: number) => {
            const newImages = [...images];
            newImages.splice(index, 1);
            onChange(newImages);
        };

        return (
            <div className={mergedClassName}>
                <label className="block text-sm font-medium text-gray-700">{field.label}</label>
                <div className="mt-2 space-y-2">
                    <input
                        ref={multiImageInputRef}
                        type="file"
                        accept={field.accept || 'image/*'}
                        multiple
                        onChange={handleAddImages}
                        className="hidden"
                        disabled={disabled}
                    />
                    <button
                        type="button"
                        onClick={() => multiImageInputRef.current?.click()}
                        disabled={disabled}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm transition-colors disabled:opacity-50"
                    >
                        <ImageIcon className="w-4 h-4" />
                        เพิ่มรูปภาพ
                    </button>
                    {images.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {images.map((img, index) => (
                                <div key={index} className="relative">
                                    <img
                                        src={img}
                                        alt={`Image ${index + 1}`}
                                        className="w-16 h-16 object-cover rounded-md border"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveImage(index)}
                                        disabled={disabled}
                                        className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {field.helperText && (
                        <p className="text-xs text-gray-500">{field.helperText}</p>
                    )}
                </div>
            </div>
        );
    }

    // Fallback for unsupported field types
    return (
        <div className={field.className}>
            <label className="block text-sm font-medium text-gray-700">{field.label}</label>
            <p className="text-xs text-gray-500 mt-1">Field type "{field.type}" not supported</p>
        </div>
    );
}
